import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Plus, Trash, UserPlus, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

// Reusable "Contact People" manager. `basePath` is the entity path, e.g.
// `/customers/<id>` or `/vendors/<id>`.
export function ContactsPane({ basePath, isNew, contacts = [], onChanged, testidPrefix = "entity", readOnly = false }) {
  const EMPTY = { name: "", title: "", email: "", phone: "" };
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = useMutation({
    mutationFn: async () => (await api.post(`${basePath}/contacts`, form)).data,
    onSuccess: () => { setForm(EMPTY); onChanged(); toast.success("Contact added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (cid) => api.delete(`${basePath}/contacts/${cid}`),
    onSuccess: () => { onChanged(); toast.success("Contact removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isNew) return <p className="text-sm text-muted-foreground col-span-full">Save first, then add contact people.</p>;

  return (
    <div className="col-span-full space-y-3">
      <div className="space-y-2" data-testid={`${testidPrefix}-contacts-list`}>
        {contacts.length === 0 && <p className="text-sm text-muted-foreground">No contacts yet.</p>}
        {contacts.map((c) => (
          <div key={c.id} className="flex items-start justify-between gap-3 border border-border rounded-sm px-3 py-2" data-testid={`${testidPrefix}-contact`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{c.name}{c.title ? <span className="text-muted-foreground font-normal"> · {c.title}</span> : null}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
                {c.email && <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} /> {c.email}</span>}
                {c.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {c.phone}</span>}
              </div>
            </div>
            {!readOnly && <button onClick={() => del.mutate(c.id)} className="shrink-0 p-1 text-muted-foreground hover:text-destructive" data-testid={`${testidPrefix}-contact-delete`} title="Remove contact"><Trash size={15} /></button>}
          </div>
        ))}
      </div>

      {!readOnly && <div className="border border-dashed border-border rounded-sm p-3 bg-secondary/30">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="overline-label">Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-contact-name`} /></div>
          <div><Label className="overline-label">Role / Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-contact-title`} /></div>
          <div><Label className="overline-label">Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-contact-email`} /></div>
          <div><Label className="overline-label">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-contact-phone`} /></div>
        </div>
        <Button onClick={() => add.mutate()} disabled={!form.name.trim() || add.isPending} className="mt-3 rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs" data-testid={`${testidPrefix}-add-contact`}><UserPlus size={14} className="mr-1" /> Add Contact</Button>
      </div>}
    </div>
  );
}
